import { LoadingOverlay } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import React, {
  createContext,
  useCallback,
  useContext, useEffect, useLayoutEffect,
  useMemo,
  useState
} from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { AUTH_LOGIN } from "../services/auth";
import { CHANGE_USER_STATUS, GET_SELF_PROFILE, GET_USER_DATA } from "../services/users";
import { RouteProps } from "../utility/routes";

interface AuthContextProps {
  user: any;
  userData: any;
  planData: any;
  currentCompany: {company: any; role?: any};
  onChangeCompany: (companyId : string, refresh? : boolean) => void;
  loadUserData: () => void;
  companies: any[];
  teams: any[];
  hasPermission: (route : RouteProps) => boolean;
  changeStatus: (status: string) => void;
  logout: () => void;
  login: (
    email: string,
    password: string,
    callback?: () => void
  ) => Promise<void>;
  signIn: (
    user : any,
    callback?: () => void
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const localCompany = localStorage.getItem("@satify-admin/company");
  const lsUser = localStorage.getItem("@satify-admin/user");
  const [isLoading, setIsLoading] = useState(false);
  const [showChildren, setShowChildren] = useState(true);
  const [loadedCompany, setLoadedCompany] = useState(false);

  const [user, setUser] = useState(lsUser ? JSON.parse(lsUser) : null);
  const [currentCompany, setCurrentCompany] = useState((localCompany && localCompany !== "undefined") ? JSON.parse(localCompany) : null);
  const [userData, setUserData] = useState(null);

  const [display, setDisplay] = useState(true);

  const navigate = useNavigate();

  const loadUserData = async () => {
    try{
      setUserData(await GET_USER_DATA());
    }catch(err){
      setUserData(null);
      setCurrentCompany(null);
    }
  }

  useEffect(() => {
    loadUserData();
  }, [user])

  useEffect(() => {
    if((!currentCompany || !loadedCompany) && userData?.user?.currentCompany){
      setCurrentCompany(userData.user.currentCompany);
      setLoadedCompany(true);
    }
  }, [userData, currentCompany, loadedCompany])

  useEffect(() => {
    localStorage.setItem("@satify-admin/company", JSON.stringify(currentCompany));
  }, [currentCompany])

  const signIn = async (user, callback) => {
    localStorage.setItem("@satify-admin/user", JSON.stringify(user));
    setUser(user);
    setIsLoading(false);
    callback();
  }

  const login = useCallback(
    async (email: string, password: string, callback?: () => void) => {
      setIsLoading(true);
      try {
        const {user} = (await AUTH_LOGIN({ email, password }));
        signIn(user, callback);
      } catch (err) {
        setIsLoading(false);
        showNotification({
          color: "red",
          title: "Erro!",
          message: "Falha ao realizar o login. Verifique seu email e senha.",
        });
        console.error({ err });
      }
    },
    []
  );

  const hasPermission = (route : RouteProps) => {
    return true
    return !route.private
      || (
        userData && 
        (userData.resources || []).some(r => r.key === route.key)
      )
  }

  const logout = () => {
    localStorage.removeItem('@satify-admin/user');
    setUser(null)
  }

  const onChangeCompany = (companyId, refresh = true) => {
    if(refresh){
      setShowChildren(false);
      setTimeout(() => {
        setShowChildren(true);
        // navigate("/")
      }, 10);
    }
    const currentCompany = (userData?.user?.companies || []).find(r => `${r.company._id}` === `${companyId}`);
    if(currentCompany) setCurrentCompany(currentCompany);
  }

  const planData = userData?.plan;

  const changeStatus = (status) => {
    CHANGE_USER_STATUS(status)
    .then(() => {})
    .catch(() => {})
  }

  const value = useMemo<AuthContextProps>(() => {
    return {
      user,
      userData,
      loadUserData,
      onChangeCompany,
      planData,
      currentCompany,
      teams: userData?.user?.teams || [],
      companies: (userData?.user?.companies || []),
      changeStatus,
      signIn, login, hasPermission, logout };
  }, [user, userData, planData, login, signIn, hasPermission]);

  useEffect(() => {
    setDisplay(false);
    setTimeout(() => setDisplay(true), 100);
  }, [currentCompany])

  if(!display) return null;
  return (
    <AuthContext.Provider value={value}>
      <LoadingOverlay visible={isLoading} />
      {showChildren && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
